// Site developed and built by sitethreesixty.com
// https://www.sitethreesixty.com

import { useState } from 'react'
import { NavLink, Link } from 'react-router'
import { MoreHorizontal, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { getMobilePrimaryItems, getNavigationIcon, getOverflowItems } from '@/lib/navigation'
import { useAuth } from '@/providers/auth-provider'

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * PortalBottomTabs — mobile bottom tab bar (visible below 768px).
 *
 * Fixed to the bottom of the viewport. Hidden on desktop (md: and above).
 *
 * 4 primary tabs + "More" button:
 * - Home → /portal
 * - Blog → /portal/blog
 * - Invoices → /portal/invoices
 * - Community → /portal/community
 * - More → opens a Sheet with all remaining nav items
 *
 * The "More" Sheet provides access to: Newsletter, Changes, Alerts, Insights,
 * Perks, Referrals, FAQ, Resources, Vault, Settings.
 *
 * Active tab highlighted with primary colour.
 * Tapping a More sheet item closes the sheet and navigates.
 */
export function PortalBottomTabs() {
  const { user } = useAuth()
  const [moreOpen, setMoreOpen] = useState(false)
  const primaryTabs = getMobilePrimaryItems(user)
  const moreItems = getOverflowItems(user)

  return (
    <>
      {/* ── Tab bar ─────────────────────────────────────────────────── */}
      <nav
        className="fixed inset-x-0 bottom-0 z-50 px-3 pb-3 md:hidden"
        aria-label="Mobile navigation"
      >
        <div className="mx-auto flex max-w-sm rounded-[20px] border border-border/80 bg-background/94 p-1.5 shadow-[0_18px_45px_rgba(15,23,42,0.18)] backdrop-blur">
          {/* Primary tabs */}
          {primaryTabs.map(({ icon, label, href }) => {
            const Icon = getNavigationIcon(icon)
            return (
            <NavLink
              key={href}
              to={href}
              end={href === '/portal'}
              className={({ isActive }) =>
                cn(
                  'flex flex-1 flex-col items-center justify-center gap-1 rounded-[14px] py-2 text-xs transition-colors',
                  isActive
                    ? 'bg-primary text-primary-foreground shadow-sm'
                    : 'text-muted-foreground hover:bg-muted/70 hover:text-foreground'
                )
              }
            >
              <Icon className="h-5 w-5" />
              <span>{label}</span>
            </NavLink>
            )
          })}

          {/* More button — opens Sheet */}
          {moreItems.length > 0 && (
            <button
              type="button"
              onClick={() => setMoreOpen(true)}
              className={cn(
                'flex flex-1 flex-col items-center justify-center gap-1 rounded-[14px] py-2 text-xs transition-colors',
                moreOpen
                  ? 'bg-primary text-primary-foreground shadow-sm'
                  : 'text-muted-foreground hover:bg-muted/70 hover:text-foreground'
              )}
              aria-label="More navigation options"
              aria-expanded={moreOpen}
            >
              <MoreHorizontal className="h-5 w-5" />
              <span>More</span>
            </button>
          )}
        </div>
      </nav>

      {/* ── More Sheet ──────────────────────────────────────────────── */}
        <Sheet open={moreOpen} onOpenChange={setMoreOpen}>
        <SheetContent side="bottom" className="pb-safe rounded-t-[20px]">
          <SheetHeader className="mb-4">
            <div className="flex items-center justify-between">
              <SheetTitle className="text-base">More</SheetTitle>
              <button
                type="button"
                onClick={() => setMoreOpen(false)}
                className="rounded-md p-1 text-muted-foreground hover:text-foreground transition-colors"
                aria-label="Close"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </SheetHeader>

          <nav aria-label="More navigation">
            <div className="grid grid-cols-4 gap-2">
          {moreItems.map(({ icon, label, href }) => {
            const Icon = getNavigationIcon(icon)
            return (
                <Link
                  key={href}
                  to={href}
                  onClick={() => setMoreOpen(false)}
                  className="flex flex-col items-center gap-1.5 rounded-xl px-2 py-3 text-center hover:bg-muted transition-colors"
                >
                  <div className="rounded-full bg-muted p-2">
                    <Icon className="h-4 w-4 text-foreground" />
                  </div>
                  <span className="text-xs text-muted-foreground leading-tight">{label}</span>
                </Link>
            )
          })}
            </div>
          </nav>
        </SheetContent>
      </Sheet>
    </>
  )
}

// https://www.sitethreesixty.com
